import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { generateRecoveryKey, getSessionKeys, wrapForRecoveryKey } from '../../../services/chat/chatKeys';
import { listPasskeys, removePasskey } from '../../../services/chat/chatPasskeys';
import { bumpTrustEpoch, rewrapRecoveryKey } from '../../../services/chat/chatService';
import type { PasskeyInfo, UserKeys } from '../../../services/chat/chatService';
import { setTrustedEpoch } from '../../../services/chat/chatTrustedBrowser';
import { AddPasskeyDialog } from './AddPasskeyDialog';
import { RecoveryKeyStep } from './RecoveryKeyStep';
import { SettingsGateCancelled, isReauthRequired, useSettingsGate } from './useSettingsGate';

export const TRUST_EPOCH_CONFIRMATION =
  'Other browsers will ask for your passkey or recovery key the next time they open chat.';
export const LAST_PASSKEY_MESSAGE = "You can't remove your last passkey. Add another one first.";
export const NEW_RECOVERY_KEY_DONE = 'Your new recovery key is active. The old recovery key no longer unlocks chat.';
export const REMOVE_PASSKEY_WARNING =
  'This passkey will no longer unlock chat. Deleting a passkey from your Apple or Google account also removes its ability to unlock chat.';

function formatDate(iso: string | null): string {
  if (!iso) return 'Never used';
  const time = Date.parse(iso);
  return Number.isNaN(time) ? iso : new Date(time).toLocaleDateString();
}

export interface ChatKeySettingsProps {
  open: boolean;
  uid: string;
  email: string;
  keys: UserKeys;
  onClose: () => void;
  onKeysChange: (keys: UserKeys) => void;
}

type View = 'main' | 'new-recovery-key';

/**
 * Chat encryption settings (unlocked only): passkeys (list, add, remove),
 * "Make a new recovery key", and "Sign out of all other trusted browsers".
 * Every write satisfies the settings gate first; a 403 re-runs the gate once.
 */
export function ChatKeySettings({ open, uid, email, keys, onClose, onKeysChange }: ChatKeySettingsProps) {
  const gate = useSettingsGate({ uid, email, passkeyCount: keys.passkeys.length });
  const [view, setView] = useState<View>('main');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<PasskeyInfo | null>(null);
  const [newRecoveryKey, setNewRecoveryKey] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setView('main');
    setError(null);
    setStatus(null);
    setNewRecoveryKey(null);
    listPasskeys()
      .then((passkeys) => onKeysChange({ ...keys, passkeys }))
      .catch(() => {
        /* the list from GET /keys/me stays */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const withGate = async <T,>(write: () => Promise<T>): Promise<T> => {
    await gate.ensureGate();
    try {
      return await write();
    } catch (err) {
      if (!isReauthRequired(err)) throw err;
      await gate.ensureGate({ force: true });
      return write();
    }
  };

  const run = async (action: string, task: () => Promise<void>) => {
    setBusy(action);
    setError(null);
    setStatus(null);
    try {
      await task();
    } catch (err: any) {
      if (!(err instanceof SettingsGateCancelled)) {
        setError(err?.message || 'The change could not be saved. Please try again.');
      }
    } finally {
      setBusy(null);
    }
  };

  const refreshPasskeys = async () => {
    const passkeys = await listPasskeys();
    onKeysChange({ ...keys, passkeys });
  };

  /** Runs after the confirmation has closed, so the gate's sign-in step and any error show in this dialog. */
  const handleRemove = (target: PasskeyInfo) =>
    run('remove', async () => {
      try {
        await withGate(() => removePasskey(target.credentialId));
      } catch (err: any) {
        if (err?.status === 409) throw new Error(LAST_PASSKEY_MESSAGE);
        throw err;
      }
      await refreshPasskeys();
      setStatus(`Passkey "${target.name}" removed.`);
    });

  const handleStartNewRecoveryKey = () =>
    run('recovery', async () => {
      await gate.ensureGate();
      setNewRecoveryKey(generateRecoveryKey());
      setView('new-recovery-key');
    });

  const handleRecoveryConfirmed = () =>
    run('recovery', async () => {
      const session = getSessionKeys();
      if (!session || !newRecoveryKey) throw new Error('Chat is locked. Unlock it before changing the recovery key.');
      const wrapped = await wrapForRecoveryKey(session.privateKey, newRecoveryKey);
      const next = await withGate(() => rewrapRecoveryKey(wrapped));
      onKeysChange({ ...keys, ...next, passkeys: next.passkeys.length ? next.passkeys : keys.passkeys });
      setNewRecoveryKey(null);
      setView('main');
      setStatus(NEW_RECOVERY_KEY_DONE);
    });

  const handleSignOutOthers = () =>
    run('trust-epoch', async () => {
      const trustEpoch = await withGate(() => bumpTrustEpoch());
      await setTrustedEpoch(uid, trustEpoch);
      onKeysChange({ ...keys, trustEpoch });
      setStatus(TRUST_EPOCH_CONFIRMATION);
    });

  const closing = busy != null || gate.signingIn;

  return (
    <Dialog open={open} onClose={closing ? undefined : onClose} maxWidth="sm" fullWidth className="ph-no-capture" PaperProps={{ className: 'ph-no-capture' }} aria-labelledby="chat-key-settings-title">
      <DialogTitle id="chat-key-settings-title" sx={{ fontWeight: 600 }}>
        Chat encryption settings
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        {gate.signInStep ? (
          gate.signInStep
        ) : view === 'new-recovery-key' && newRecoveryKey ? (
          <RecoveryKeyStep
            recoveryKey={newRecoveryKey}
            email={email}
            onConfirmed={handleRecoveryConfirmed}
            confirmLabel="Activate new recovery key"
            disabled={busy != null}
          />
        ) : (
          <>
            {status && <Alert severity="success">{status}</Alert>}
            {error && <Alert severity="error">{error}</Alert>}

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Passkeys
              </Typography>
              <List dense aria-label="Passkeys" disablePadding>
                {keys.passkeys.map((passkey) => (
                  <ListItem
                    key={passkey.credentialId}
                    divider
                    secondaryAction={
                      <Button
                        size="small"
                        color="error"
                        disabled={busy != null}
                        onClick={() => setRemoveTarget(passkey)}
                        sx={{ textTransform: 'none' }}
                      >
                        Remove
                      </Button>
                    }
                  >
                    <ListItemText
                      primary={passkey.name}
                      secondary={`Added ${formatDate(passkey.createdAt)} · ${passkey.lastUsedAt ? `Last used ${formatDate(passkey.lastUsedAt)}` : 'Never used'}`}
                    />
                  </ListItem>
                ))}
              </List>
              <Button
                size="small"
                variant="outlined"
                disabled={busy != null || keys.passkeys.length >= 5}
                onClick={() => setAddOpen(true)}
                sx={{ mt: 1, textTransform: 'none' }}
              >
                Add a passkey
              </Button>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Recovery key
              </Typography>
              <DialogContentText sx={{ fontSize: '0.875rem', mb: 1 }}>
                A new recovery key replaces the old file; the old key stops working.
              </DialogContentText>
              <Button size="small" variant="outlined" disabled={busy != null} onClick={handleStartNewRecoveryKey} sx={{ textTransform: 'none' }}>
                {busy === 'recovery' ? <CircularProgress size={18} /> : 'Make a new recovery key'}
              </Button>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Trusted browsers
              </Typography>
              <DialogContentText sx={{ fontSize: '0.875rem', mb: 1 }}>
                This browser stays trusted. Every other browser will need your passkey or recovery key again.
              </DialogContentText>
              <Button size="small" variant="outlined" disabled={busy != null} onClick={handleSignOutOthers} sx={{ textTransform: 'none' }}>
                {busy === 'trust-epoch' ? <CircularProgress size={18} /> : 'Sign out of all other trusted browsers'}
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={() => {
            if (view === 'new-recovery-key') {
              setNewRecoveryKey(null);
              setView('main');
            } else onClose();
          }}
          disabled={closing}
        >
          {view === 'new-recovery-key' ? 'Cancel' : 'Close'}
        </Button>
      </DialogActions>

      <AddPasskeyDialog
        open={addOpen}
        uid={uid}
        email={email}
        prfSalt={keys.prfSalt}
        passkeyCount={keys.passkeys.length}
        onClose={(added) => {
          setAddOpen(false);
          if (added) {
            onKeysChange({ ...keys, passkeys: [...keys.passkeys, added] });
            setStatus(`Passkey "${added.name}" added.`);
          }
        }}
      />

      <Dialog
        open={removeTarget != null}
        onClose={busy ? undefined : () => setRemoveTarget(null)}
        maxWidth="xs"
        fullWidth
        className="ph-no-capture" PaperProps={{ className: 'ph-no-capture' }}
        aria-labelledby="remove-passkey-title"
      >
        <DialogTitle id="remove-passkey-title" sx={{ fontWeight: 600 }}>
          Remove passkey "{removeTarget?.name}"?
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <Alert severity="warning">{REMOVE_PASSKEY_WARNING}</Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRemoveTarget(null)} disabled={busy != null} sx={{ color: 'text.secondary' }}>
            Keep
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy != null}
            onClick={() => {
              const target = removeTarget;
              setRemoveTarget(null);
              if (target) void handleRemove(target);
            }}
          >
            Remove passkey
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
